import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { saveDefaults } from "@/lib/defaults";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { CreditCard, Check } from "lucide-react";

const CARD_W_MM = 85.6; // ISO/IEC 7810 ID-1
const CARD_RATIO = 85.6 / 53.98; // ~1.585

export const CalibrationDialog = ({ pxPerMm, onCalibrated, trigger }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(pxPerMm ? Math.round(pxPerMm * CARD_W_MM) : 323);

  const confirm = () => {
    const ppm = width / CARD_W_MM;
    saveDefaults({ calibrationPxPerMm: ppm });
    onCalibrated(ppm);
    setOpen(false);
    toast.success(t("cal_toast_title"), { description: t("cal_toast_desc") });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("cal_title")}</DialogTitle>
          <DialogDescription>{t("cal_desc")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-6 py-3">
          <div
            className="rounded-lg border-2 border-primary bg-gradient-to-br from-sky-600 to-sky-800 shadow-md flex items-end p-3"
            style={{ width: `${width}px`, height: `${width / CARD_RATIO}px` }}
            data-testid="calibration-card"
          >
            <CreditCard className="w-8 h-8 text-white/80" />
          </div>
          <div className="w-full max-w-sm">
            <Slider min={150} max={760} step={1} value={[width]} onValueChange={(v) => setWidth(v[0])} data-testid="calibration-slider" />
            <div className="mt-2 flex justify-between text-xs text-slate-400">
              <span>{t("cal_smaller")}</span>
              <span className="font-semibold text-slate-600" data-testid="calibration-ppm">{(width / CARD_W_MM).toFixed(2)} px/mm</span>
              <span>{t("cal_larger")}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={confirm} data-testid="calibration-confirm">
            <Check className="w-4 h-4" /> {t("cal_confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
